import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ShakaVideoTile from "@/components/ShakaVideoTile";
import { Volume2, VolumeX, Radio, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LOGO_POS = {
  "top-left": "top-3 left-3",
  "top-right": "top-3 right-3",
  "bottom-left": "bottom-3 left-3",
  "bottom-right": "bottom-3 right-3",
};

// Public, no-login channel player. Streams the channel's current R2 HLS ladder
// (adaptive) or flat MP4 with a Now/Next overlay. Polls for slot changes.
export default function Watch() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const lastUrlRef = useRef(null);

  const changeVolume = (v) => {
    setVolume(v);
    setMuted(v <= 0);
  };
  const toggleMute = () => setMuted((m) => !m);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND}/api/public/watch/${slug}`);
      if (!r.ok) {
        setError(r.status === 404 ? "This channel is not available." : "Unable to load the stream.");
        return;
      }
      const j = await r.json();
      setError(null);
      // Only replace state (and thus the player URL) when the URL actually
      // changed — avoids re-buffering the tile every poll.
      setData((prev) => {
        if (prev && prev.playback_url === j.playback_url) {
          return { ...j, playback_url: prev.playback_url };
        }
        lastUrlRef.current = j.playback_url;
        return j;
      });
    } catch (_) {
      setError("Unable to reach the stream.");
    }
  }, [slug]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  // Keep the whole document dark for the public player so overscroll / short
  // content never flashes the global light body background.
  useEffect(() => {
    const body = document.body;
    const prevBg = body.style.background;
    body.style.background = "#0b0b0f";
    return () => { body.style.background = prevBg; };
  }, []);

  // Public player must stay dark regardless of the tenant theme. The global
  // light-theme override only rewrites `.bg-black*`, so we use an arbitrary
  // colour class it can't match instead of trying to flip data-theme (an
  // ancestor ThemeProvider effect would overwrite that on mount).

  const ch = data?.channel;
  const accent = ch?.color || "#EF4444";

  return (
    <div className="min-h-screen bg-[#0b0b0f] flex flex-col" style={{ colorScheme: "dark", color: "#ffffff" }} data-testid="public-watch-page">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          {ch?.logo_url
            ? <img data-testid="watch-header-logo" src={ch.logo_url} alt="" className="h-7 w-auto object-contain" />
            : <span className="h-7 w-7 rounded-sm grid place-items-center font-black text-xs" style={{ background: accent }}>{(ch?.name || "?").slice(0, 2).toUpperCase()}</span>}
          <div className="min-w-0">
            <div className="font-display font-bold text-sm truncate" data-testid="watch-channel-name">{ch?.name || "Loading…"}</div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest" style={{ color: accent }}>
              <Radio className="w-3 h-3 animate-pulse" /> Live
            </div>
          </div>
        </div>
        <button
          onClick={toggleMute}
          data-testid="watch-mute-toggle"
          className="rounded-full h-9 w-9 grid place-items-center bg-white/10 hover:bg-white/20 transition-colors"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </header>

      {/* Player */}
      <main className="flex-1 flex items-center justify-center relative">
        <div className="w-full max-w-6xl aspect-video relative">
          {error ? (
            <div className="absolute inset-0 grid place-items-center text-center px-6" data-testid="watch-error">
              <div>
                <div className="text-sm font-mono text-white/70">{error}</div>
              </div>
            </div>
          ) : !data ? (
            <div className="absolute inset-0 grid place-items-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/50" />
            </div>
          ) : data.playback_url ? (
            <>
              <ShakaVideoTile
                url={data.playback_url}
                brandName={ch?.name}
                brandColor={accent}
                muted={muted}
                volume={volume}
                poster={data.now?.poster}
                testId="watch-player"
              />
              {/* On-player volume control — mute toggle + slider overlaid on the
                  video so viewers can adjust sound without hunting the header. */}
              <div
                className="absolute bottom-3 left-3 z-30 flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-2.5 py-1.5 border border-white/10"
                data-testid="watch-volume-control"
              >
                <button
                  onClick={toggleMute}
                  data-testid="watch-player-mute-btn"
                  aria-label={muted ? "Unmute" : "Mute"}
                  className="grid place-items-center text-white/90 hover:text-white transition-colors"
                >
                  {muted || volume <= 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(e) => changeVolume(parseFloat(e.target.value))}
                  data-testid="watch-volume-slider"
                  aria-label="Volume"
                  className="w-20 sm:w-28 h-1 accent-white cursor-pointer"
                  style={{ accentColor: accent }}
                />
              </div>
              {ch?.logo_url && (
                <img
                  src={ch.logo_url}
                  alt=""
                  data-testid="watch-logo-bug"
                  className={`absolute z-20 pointer-events-none object-contain drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)] ${LOGO_POS[ch.logo_position] || LOGO_POS["top-right"]}`}
                  style={{ width: `${ch.logo_size_pct || 14}%`, opacity: ch.logo_opacity ?? 0.9 }}
                />
              )}
            </>
          ) : (
            <div className="absolute inset-0 grid place-items-center text-center px-6" data-testid="watch-offair">
              <div className="text-sm font-mono text-white/60">This channel is off air right now.</div>
            </div>
          )}
        </div>
      </main>

      {/* Now / Next strip */}
      {data && (
        <footer className="px-4 sm:px-6 py-3 border-t border-white/10 grid grid-cols-2 gap-4" data-testid="watch-now-next">
          <div className="min-w-0">
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-0.5">Now playing</div>
            <div className="text-sm font-semibold truncate" data-testid="watch-now-title">{data.now?.title || "—"}</div>
          </div>
          <div className="min-w-0 text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-0.5">Up next</div>
            <div className="text-sm text-white/70 truncate" data-testid="watch-next-title">{data.next?.title || "—"}</div>
          </div>
        </footer>
      )}
    </div>
  );
}
